import { LinearProgress, Stack, Typography } from '@mui/material';
import _ from 'lodash';
import {
  UIEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DetailedSearch } from '../../../components/search-input/DetailedSearchBar';
import {
  GrayButton,
  PrimaryButton,
  PrimaryColorButton,
  PrimaryOutlinedButton,
  SecondaryTextButton,
} from '../../../components/style/ButtonStyle';
import {
  CountingText,
  DetailInfoText,
  FormTitle,
  ListTitleText,
} from '../../../components/style/TextStyle';
import {
  AddButtonIcon,
  DeleteIcon,
  DownloadFileIcon,
  DownloadInfoIcon,
  EmptyIcon,
  PencilIcon,
} from '../../../layouts/dashboard/nav/IconButton';

import {
  QueryClient,
  QueryClientProvider,
  useInfiniteQuery,
} from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { MaterialReactTable } from 'material-react-table';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDeepCompareEffect } from 'react-use';
import 'simplebar-react/dist/simplebar.min.css';
import * as Yup from 'yup';
import { ApiResultModal } from '../../../components/modal/ApiResultHandler';
import { ApproveModal } from '../../../components/modal/ApproveModal';
import { ModalText, useModalToggle } from '../../../components/modal/Modal';
import BasicDatePicker from '../../../components/select/DatePicker';
import {
  DepartmentSelect,
  TypeSelect,
} from '../../../components/select/SelectList';
import {
  EmptyBackground,
  TableBackground,
} from '../../../components/style/CommonStyle';
import { PrettyScrollbarTableWrap } from '../../../components/style/TableStyle';
import { useAuthAxios } from '../../../hooks/useAuthAxios';
import {
  FormErrorModal,
  useYupValidationResolver,
} from '../../../hooks/useYupValidationResolver';
import { selectAccessToken } from '../../../store/authSlice';
import { APP_BAR_DESKTOP, OwnershipTypeListLabel } from '../../../utils/enum';
import { fDate } from '../../../utils/formatTime';
import SelectedListCard from './SelectedListCard';
import { sliceSerialNumber } from '../../../utils/helper';

const queryClient = new QueryClient();

export default function FacilityListPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <FacilityList />
    </QueryClientProvider>
  );
}

const PAGE_SIZE = 3000;

function FacilityList() {
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(new Date());
  const [department, setDepartment] = useState('');

  const navigate = useNavigate();
  const params = {
    searchText: search,
    categoryId: type,
    dateOfInstallStart: fDate(from, 'yyyy-MM-dd'),
    dateOfInstallEnd: fDate(to, 'yyyy-MM-dd'),
    maintenanceDepartmentId: department,
  };
  const accessToken = useSelector(selectAccessToken);
  const tanstackResult = useInfiniteQuery<AxiosResponse>({
    queryKey: [params],
    queryFn: async ({ pageParam = 0 }) => {
      const resp = await axios({
        method: 'GET',
        url: '/api/v1/facility',
        params: {
          ...params,
          page: pageParam + 1,
          pageSize: PAGE_SIZE,
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      return resp.data;
    },
    getNextPageParam: (_lastGroup, groups) => groups.length,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
  const { data, fetchNextPage, isFetching, isLoading } = tanstackResult;

  const facilities = useMemo(() => {
    const list = data?.pages.flatMap(page => _.get(page, 'result', [])) || [];
    // const emptyRows = _.times(PAGE_SIZE - list.length, t => ({
    //   facilityId: t,
    // }));
    return [...list];
  }, [data]);

  const totalDBRowCount = _.get(data, 'pages.0.count', 0);
  const totalFetched = facilities.length;

  //called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        //once the user has scrolled within 400px of the bottom of the table, fetch more data if we can
        if (
          scrollHeight - scrollTop - clientHeight < 400 &&
          !isFetching &&
          totalFetched < totalDBRowCount
        ) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount],
  );

  const [facilitySelectionTable, setFacilitySelectionTable] = useState({});
  const facilitySelectionList = _.values(facilitySelectionTable);

  const [excelSelectionTable, setExcelSelectionTable] = useState({});
  const excelSelectionList = _.values(excelSelectionTable);
  const isExcelSelectionEmpty = _.isEmpty(excelSelectionList);

  const handleAddToExcelSelection = () => {
    const itemsToAdd = _.difference(facilitySelectionList, excelSelectionList);
    const newExcelSelectionTable = { ...excelSelectionTable };

    itemsToAdd.forEach((item: any) => {
      newExcelSelectionTable[item?.facilityId] = item;
    });

    setExcelSelectionTable(newExcelSelectionTable);
  };

  return (
    <Stack
      mx={4}
      spacing={5}
      py={8}
      sx={{ height: `calc(100vh - ${APP_BAR_DESKTOP}px)` }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <FormTitle>시설물 관리</FormTitle>
        <Stack direction="row" spacing={2}>
          <PrimaryOutlinedButton
            startIcon={<PencilIcon />}
            onClick={() => navigate(`/facility/bulk`)}
            sx={{ width: '200px' }}
          >
            시설물 일괄 등록
          </PrimaryOutlinedButton>
          <PrimaryButton
            startIcon={<AddButtonIcon />}
            onClick={() => navigate(`/address?`)}
          >
            새 시설물 등록
          </PrimaryButton>
        </Stack>
      </Stack>
      <Stack direction="row" spacing={3} sx={{ height: '100%' }}>
        <Stack flex={3} spacing={3}>
          <Stack direction="row" spacing={3} justifyContent="space-between">
            <ListTitleText>시설물 관리 리스트</ListTitleText>
            <Stack justifyContent="center">
              <a
                href="https://drive.google.com/file/d/1ZxjBphS14EkISQpHiQ4qinCHE7pGLTWa/view?usp=drive_link"
                target="_blank"
                rel="noreferrer"
              >
                <SecondaryTextButton
                  startIcon={<DownloadInfoIcon />}
                  endIcon={<DownloadFileIcon />}
                >
                  시설물 현황 관리 가이드
                </SecondaryTextButton>
              </a>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={2}>
            <DetailedSearch
              placeholder="위치,시설명 또는 관리번호로 검색"
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  setSearch(e.target.value);
                }
              }}
              onClick={(e, value) => {
                setSearch(value);
              }}
            />
            <Stack direction="row" alignItems="center" spacing={2}>
              <BasicDatePicker date={from} setDate={setFrom} />
              <Typography>-</Typography>
              <BasicDatePicker date={to} setDate={setTo} />
            </Stack>
            <TypeSelect
              type={type}
              onChange={(e: any) => setType(e.target.value)}
            />
            <DepartmentSelect
              department={department}
              onChange={(e: any) => setDepartment(e.target.value)}
            />
          </Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <CountingText>총 {totalDBRowCount}건 검색</CountingText>
              <DeleteFacilitiesButton selectTable={facilitySelectionTable} />
            </Stack>
            <PrimaryColorButton
              startIcon={<AddButtonIcon />}
              onClick={handleAddToExcelSelection}
            >
              추가
            </PrimaryColorButton>
          </Stack>
          {_.isEmpty(facilities) && !isLoading ? (
            <EmptyBackground
              justifyContent="center"
              alignItems="center"
              flex={1}
            >
              <Stack alignItems="center" spacing={1.5}>
                <EmptyIcon />
                <DetailInfoText>검색 결과가 없습니다.</DetailInfoText>
              </Stack>
            </EmptyBackground>
          ) : (
            <FacilityTable
              facilities={facilities}
              isFetching={isFetching}
              isLoading={isLoading}
              selectionTable={_.mapValues(facilitySelectionTable, () => true)}
              onSelectionChange={newSelection => {
                const facilityTable = _.keyBy(facilities, 'facilityId');
                const newSelectionTable = {};
                _.keys(newSelection).forEach(k => {
                  newSelectionTable[k] = facilityTable[k];
                });
                setFacilitySelectionTable(newSelectionTable);
              }}
              fetchMoreOnBottomReached={fetchMoreOnBottomReached}
            />
          )}
        </Stack>
        {isExcelSelectionEmpty ? (
          <TableBackground justifyContent="center" alignItems="center" flex={1}>
            <Stack alignItems="center" spacing={1.5}>
              <EmptyIcon />
              <DetailInfoText>선택된 리스트가 없습니다.</DetailInfoText>
            </Stack>
          </TableBackground>
        ) : (
          <SelectedListCard
            selectedList={excelSelectionList}
            setSelectTable={setExcelSelectionTable}
            selectTable={excelSelectionTable}
          />
        )}
      </Stack>
    </Stack>
  );
}

function FacilityTable({
  facilities,
  isFetching,
  isLoading,
  selectionTable,
  onSelectionChange,
  fetchMoreOnBottomReached,
}) {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  //a check on mount to see if the table is already scrolled to the bottom and immediately needs to fetch more data
  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'serialNumber',
        header: '관리번호',
        Cell: ({ row }: any) => {
          const facility = row.original;
          return <>{sliceSerialNumber(facility?.serialNumber) || ''}</>;
        },
      },
      {
        accessorKey: 'category',
        header: '구분',
      },
      {
        accessorKey: 'title',
        header: '시설명',
        size: 320,
      },
      {
        accessorKey: 'subCategory',
        header: '기구명',
      },
      {
        accessorKey: 'addressName',
        header: '주소',

        size: 320,
      },
      {
        accessorKey: 'addressNote',
        header: '위치참고',
      },
      {
        accessorKey: 'inspectionScore',
        header: '상태등급',
      },

      {
        accessorKey: 'dateOfInstall',
        header: '설치일',
        Cell: ({ cell }) => <>{fDate(cell.getValue(), 'yyyy-MM-dd') || '-'}</>,
      },

      {
        accessorKey: 'ownershipType',
        header: '설치유형',
        Cell: ({ row }: any) => {
          const facility = row.original;
          return (
            <>{_.get(OwnershipTypeListLabel, facility?.ownershipType, '-')}</>
          );
        },
      },
      {
        accessorKey: 'usedAs',
        header: '유형상세',
        Cell: ({ row }: any) => {
          const facility = row.original;
          return (
            <>{facility?.usedAs === null ? '' : facility?.usedAs || '-'}</>
          );
        },
      },
      {
        accessorKey: 'occupyingSpace',
        header: '설치면적',
      },
      {
        accessorKey: 'budget',
        header: '사업비(국비/시비/구비)',
        Cell: ({ row }: any) => {
          const facility = row.original;
          return (
            <>
              {facility?.installCostNation || '-'}/
              {facility?.installCostCity || '-'}/
              {facility?.installCostDistrict || '-'}
            </>
          );
        },
      },
      {
        accessorKey: 'installedDepartment',
        header: '설치부서/담당부서',
      },
      {
        accessorKey: 'maintenanceDepartment',
        header: '관리부서',
      },
      {
        accessorKey: 'managingDepartment',
        header: '총괄부서',
      },
      {
        accessorKey: 'contactNumber',
        header: '연락처',
      },
      {
        accessorKey: 'consignedOrgTitle',
        header: '위탁기관',
      },
      {
        accessorKey: 'during',
        header: '위탁기간',
        Cell: ({ row }: any) => {
          const facility = row.original;
          return (
            <>
              {[
                fDate(facility?.consignedStartDate, 'yyyy-MM-dd'),
                fDate(facility?.consignedEndDate, 'yyyy-MM-dd'),
              ]
                .filter(_.identity)
                .join(' ~ ') || '-'}
            </>
          );
        },
      },
      {
        accessorKey: 'notes',
        header: '비고',
        Cell: ({ row }: any) => {
          const facility = row.original;
          return <>{facility?.notes === null ? '' : facility?.notes || '-'}</>;
        },
      },
      {
        accessorKey: 'attachments',
        header: '첨부파일',

        size: 320,
      },
    ],
    [],
  );

  const rowVirtualizerInstanceRef = useRef(null);

  return (
    <PrettyScrollbarTableWrap>
      {isFetching ? <LinearProgress /> : <div style={{ height: 4 }} />}
      <MaterialReactTable
        state={{
          isLoading,
          rowSelection: selectionTable,
        }}
        initialState={{ density: 'compact' }}
        columns={columns}
        data={facilities}
        enableTopToolbar={false}
        enableBottomToolbar={false}
        enableColumnResizing={true}
        enableColumnVirtualization={false}
        enableGlobalFilterModes={false}
        enablePagination={false}
        enablePinning={false}
        enableRowNumbers={false}
        enableRowVirtualization={true}
        enableColumnActions={false}
        enableRowSelection={true}
        enableMultiRowSelection={true}
        getRowId={original => original.facilityId as string}
        onRowSelectionChange={(updater: any) => {
          const newSelection = updater(selectionTable);
          onSelectionChange(newSelection);
        }}
        enableSorting={false}
        muiTableContainerProps={{
          style: {
            maxWidth: '70vw',
            maxHeight: 'calc(100vh - 456px)',
          },
          ref: tableContainerRef,
          onScroll: (
            event: UIEvent<HTMLDivElement>, //add an event listener to the table container element
          ) => fetchMoreOnBottomReached(event.target as HTMLDivElement),
        }}
        muiSelectCheckboxProps={{ size: 'small' }}
        muiTableHeadCellProps={{ align: 'center' }}
        muiTableBodyCellProps={{ align: 'center' }}
        rowVirtualizerInstanceRef={rowVirtualizerInstanceRef} //optional
        rowVirtualizerProps={{ overscan: 20 }} //optionally customize the row virtualizer
      />
    </PrettyScrollbarTableWrap>
  );
}

function DeleteFacilitiesButton({ selectTable }) {
  const { on, open, close } = useModalToggle();

  const resolver = useYupValidationResolver(
    Yup.object().shape({
      selectedList: Yup.array().min(1, '체크 박스 선택 후 삭제가 가능합니다.'),
    }),
  );

  const form = useForm<any>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver,
    defaultValues: {
      selectedList: [],
    },
    shouldUnregister: false,
  });

  useDeepCompareEffect(() => {
    const ids = _.map(selectTable, item => _.pick(item, 'facilityId'));
    form.setValue('selectedList', ids);
  }, [selectTable]);

  const [removeResult, remove] = useAuthAxios({
    method: 'POST',
    url: `/api/v1/facility/bulkDelete`,
  });

  const onSubmit = form.handleSubmit(function () {
    open();
  });

  return (
    <>
      <GrayButton startIcon={<DeleteIcon />} onClick={onSubmit}>
        삭제
      </GrayButton>
      <ApproveModal
        on={on}
        onClose={close}
        title="시설물 관리"
        onConfirm={() => {
          const payload = {
            facilities: form.watch('selectedList'),
          };
          remove({ data: payload });
        }}
      >
        <Stack direction="row" justifyContent="center" sx={{ pt: 3, pb: 5 }}>
          <ModalText>
            해당 시설물을 삭제하시겠습니까? 삭제된 데이터는 복구가 불가능합니다.
          </ModalText>
        </Stack>
      </ApproveModal>
      <FormErrorModal form={form} title="시설물 관리" />
      <ApiResultModal
        apiResult={removeResult}
        successCode={201}
        successTitle="시설물 관리"
        successContent="시설물이 삭제되어 페이지를 새로고침합니다."
        failTitle="요청 실패"
        failContent="관리자에게 문의해주세요."
        onSuccessClose={() => {
          close();
          window.location.reload();
        }}
      />
    </>
  );
}
